import { computed, inject, Injector, Type } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';

import {
  SubmissionSetupSignalState,
  SubmissionSetupState,
} from '../../types/submission-setup.types';
import {
  WorkspaceTypeSchema,
  WorkspaceTypeAdminSchema,
  WorkspaceSubTypeV2Schema,
  WorkspaceSubTypeV2QP,
} from '../../types/workspace-types.types';
import {
  SubmissionResponse,
  StudyWorkspaceCreate,
  SiteWorkspaceCreate,
  MTRWorkspaceCreate,
  AppealsWorkspaceCreate,
} from '../../types';
import {
  SUBMISSION_SETUP_INITIAL_STATE,
  SUBMISSION_SETUP_INITIAL_STATE_TOKEN,
} from '../../constants/submission-setup.constant';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap, tap, map, catchError, of } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { SUBMISSSION_DETAILS_V2_PATH } from '../../constants';
import { StudyService } from '@data-access/studies/services';
import { RegionFilter, SitesWorkspaceQP } from '@data-access/studies';
import { SubmissionSetupService } from '@data-access/submissions/services/submission-setup.service';
import { BaseQueryParams } from '@data-access/studies/types';
import { sortColumnsByOrdinal } from '@data-access/shared/utils/sort-columns-by-ordinal.util';
import { updateColumnsSortable } from '../../../../submission-setup/utils';
import { SubmissionsService } from '@data-access/submissions/services';

/**
 * Signal store for managing submission setup state including workspace types and sub-types
 */
export const submissionSetupSignalStore = signalStore(
  withState(() => inject(SUBMISSION_SETUP_INITIAL_STATE_TOKEN)),
  withProps((store) => ({
    _injector: inject(Injector),
    _studyService: inject(StudyService),
    _submissionSetupService: inject(SubmissionSetupService),
    _subsmissionService: inject(SubmissionsService),
    store,
  })),
  withComputed(({ store }) => ({
    $workspaceTypes: computed(() => store.workspaceTypes()),
    $workspaceSubTypes: computed(() => store.workspaceSubTypes()),
    $selectedWorkspaceType: computed(() => store.selectedWorkspaceType()),
    $selectedWorkspaceSubType: computed(() => store.selectedWorkspaceSubType()),
    $isLoading: computed(() => store.isLoading()),
    $error: computed(() => store.error()),
    $totalWorkspaceTypes: computed(() => store.totalWorkspaceTypes()),
    $totalWorkspaceSubTypes: computed(() => store.totalWorkspaceSubTypes()),
    $studySites: computed(() => store.studySites()),
    $grandTotalStudySitesCount: computed(() =>
      store.grandTotalStudySitesCount(),
    ),
    $filteredStudySitesCount: computed(() => store.filteredStudySitesCount()),
    $totalStudySitesCount: computed(() => store.totalStudySitesCount()),
    $reviewLocations: computed(() => store.reviewLocations()),
    $totalReviewLocations: computed(() => store.totalReviewLocations()),
    $studies: computed(() => store.studies()),
    $totalStudies: computed(() => store.totalStudies()),
    $studiesLoading: computed(() => store.studiesLoading()),
    $studyColumns: computed(() => store.studyColumns()),
    $siteColumns: computed(() => store.siteColumns()),
    $submission: computed(() => store.submission()),
    $workspaceId: computed(() => store.workspaceId()),
    $navigationEnabled: computed(() => store.navigationEnabled()),
    $createdSubmissionResponse: computed(() =>
      store.createdSubmissionResponse(),
    ),
  })),
  withMethods(
    ({
      store,
      _studyService,
      _submissionSetupService,
      _subsmissionService,
    }) => {
      /**
       * Constructs the URL for the submission based on Version.
       */
      const _constructSubmissionURL = (intId: number): string => {
        return `${SUBMISSSION_DETAILS_V2_PATH}/${intId}`;
      };

      const _loadWorkspaceTypes = rxMethod<{
        selectedTypeId?: string;
        params?: BaseQueryParams;
      }>(
        pipe(
          tap(() => patchState(store, { isLoading: true, error: null })),
          switchMap(({ selectedTypeId, params }) =>
            _submissionSetupService.getSubmissionTypes(params).pipe(
              tapResponse({
                error: (error: Error) =>
                  patchState(store, { error, isLoading: false }),
                finalize: () => patchState(store, { isLoading: false }),
                next: (response) => {
                  const stateUpdate: Partial<SubmissionSetupState> = {
                    isLoading: false,
                    workspaceTypes: response.data,
                    totalWorkspaceTypes: response.total,
                  };

                  if (selectedTypeId) {
                    const selectedType = response.data.find(
                      (type) => type.id === selectedTypeId,
                    );
                    if (selectedType) {
                      stateUpdate.selectedWorkspaceType = selectedType;
                    }
                  }

                  patchState(store, stateUpdate);
                },
              }),
            ),
          ),
        ),
      );

      const _loadWorkspaceSubTypes = rxMethod<{
        params?: WorkspaceSubTypeV2QP;
      }>(
        pipe(
          tap(() => patchState(store, { isLoading: true, error: null })),
          switchMap(({ params }) =>
            _submissionSetupService.getSubmissionSubTypes(params).pipe(
              tapResponse({
                error: (error: Error) =>
                  patchState(store, { error, isLoading: false }),
                finalize: () => patchState(store, { isLoading: false }),
                next: (response) => {
                  patchState(store, {
                    isLoading: false,
                    workspaceSubTypes: response.data,
                    totalWorkspaceSubTypes: response.total,
                    navigationEnabled: false,
                    createdSubmissionResponse: null,
                  });
                },
              }),
            ),
          ),
        ),
      );

      const _createStudyWorkspace = rxMethod<StudyWorkspaceCreate>(
        pipe(
          tap(() =>
            patchState(store, {
              isLoading: true,
              error: null,
              navigationEnabled: false,
              createdSubmissionResponse: null,
            }),
          ),
          switchMap((data) =>
            _submissionSetupService.createStudyWorkspace(data).pipe(
              tapResponse({
                error: (error: Error) =>
                  patchState(store, {
                    error,
                    isLoading: false,
                    navigationEnabled: false,
                  }),
                finalize: () => patchState(store, { isLoading: false }),
                next: (response: SubmissionResponse) => {
                  patchState(store, {
                    isLoading: false,
                    navigationEnabled: true,
                    createdSubmissionResponse: response,
                  });
                },
              }),
            ),
          ),
        ),
      );

      const _createSiteWorkspace = rxMethod<SiteWorkspaceCreate>(
        pipe(
          tap(() =>
            patchState(store, {
              isLoading: true,
              error: null,
              navigationEnabled: false,
              createdSubmissionResponse: null,
            }),
          ),
          switchMap((data) =>
            _submissionSetupService.createSiteWorkspace(data).pipe(
              tapResponse({
                error: (error: Error) =>
                  patchState(store, {
                    error,
                    isLoading: false,
                    navigationEnabled: false,
                  }),
                finalize: () => patchState(store, { isLoading: false }),
                next: (response: SubmissionResponse) => {
                  patchState(store, {
                    isLoading: false,
                    navigationEnabled: true,
                    createdSubmissionResponse: response,
                  });
                },
              }),
            ),
          ),
        ),
      );

      const _createMTRWorkspace = rxMethod<MTRWorkspaceCreate>(
        pipe(
          tap(() =>
            patchState(store, {
              isLoading: true,
              error: null,
              navigationEnabled: false,
              createdSubmissionResponse: null,
            }),
          ),
          switchMap((data) =>
            _submissionSetupService.createMTRWorkspace(data).pipe(
              tapResponse({
                error: (error: Error) =>
                  patchState(store, {
                    error,
                    isLoading: false,
                    navigationEnabled: false,
                  }),
                finalize: () => patchState(store, { isLoading: false }),
                next: (response: SubmissionResponse) => {
                  patchState(store, {
                    isLoading: false,
                    navigationEnabled: true,
                    createdSubmissionResponse: response,
                  });
                },
              }),
            ),
          ),
        ),
      );

      const _createAppealWorkspace = rxMethod<AppealsWorkspaceCreate>(
        pipe(
          tap(() =>
            patchState(store, {
              isLoading: true,
              error: null,
              navigationEnabled: false,
              createdSubmissionResponse: null,
            }),
          ),
          switchMap((data) => {
            const workspaceId = store.workspaceId();
            if (!workspaceId) {
              patchState(store, {
                error: new Error(
                  'Workspace ID is required to create an appeal workspace.',
                ),
                isLoading: false,
                navigationEnabled: false,
              });
              return of(null);
            }
            return _submissionSetupService
              .createAppealWorkspace(workspaceId, data)
              .pipe(
                tapResponse({
                  error: (error: Error) =>
                    patchState(store, { error, navigationEnabled: false }),
                  finalize: () =>
                    patchState(store, { isLoading: false, submission: null }),
                  next: (response: SubmissionResponse) => {
                    patchState(store, {
                      isLoading: false,
                      navigationEnabled: true,
                      createdSubmissionResponse: response,
                    });
                  },
                }),
                catchError((error: Error) => {
                  patchState(store, {
                    error,
                    isLoading: false,
                    navigationEnabled: false,
                  });
                  return of(null);
                }),
              );
          }),
        ),
      );

      const _getStudySites = rxMethod<{
        studyId: string;
        page: number;
        pageSize: number;
        query: string;
      }>(
        pipe(
          tap(() => patchState(store, { isLoading: true, error: null })),
          switchMap(({ studyId, page, pageSize, query }) =>
            _studyService
              .getSitesStudySearch({
                mtr_selection: true,
                page,
                page_size: pageSize,
                query,
                studyId,
              })
              .pipe(
                tapResponse({
                  error: (error: Error) =>
                    patchState(store, { error, isLoading: false }),
                  finalize: () => patchState(store, { isLoading: false }),
                  next: (response) => {
                    const columns = sortColumnsByOrdinal(
                      { ...response.config.columns },
                      true,
                    );
                    // Disable sorting for all columns as it is not a requirement currently
                    const updatedColumns = updateColumnsSortable(
                      columns,
                      false,
                    );
                    patchState(store, {
                      isLoading: false,
                      studySites: response.data,
                      totalStudySitesCount: response.total,
                      siteColumns: updatedColumns,
                    });
                    if (!query) {
                      patchState(store, {
                        filteredStudySitesCount: response.total,
                        grandTotalStudySitesCount: response.total,
                      });
                    }
                  },
                }),
              ),
          ),
        ),
      );

      const _getSitesForRegion = rxMethod<{
        studyId: string;
        page: number;
        pageSize: number;
        query?: string;
        regionFilter?: RegionFilter;
      }>(
        pipe(
          tap(() => patchState(store, { isLoading: true, error: null })),
          switchMap(({ studyId, page, pageSize, query, regionFilter }) =>
            _studyService
              .getSitesForStudyByRegion({
                studyId,
                page,
                page_size: pageSize,
                query,
                region_filter: regionFilter || 'both',
              })
              .pipe(
                tapResponse({
                  error: (error: Error) =>
                    patchState(store, { error, isLoading: false }),
                  finalize: () => patchState(store, { isLoading: false }),
                  next: (response) => {
                    const columns = sortColumnsByOrdinal(
                      { ...response.config.columns },
                      true,
                    );
                    // Disable sorting for all columns as it is not a requirement currently
                    const updatedColumns = updateColumnsSortable(
                      columns,
                      false,
                    );
                    patchState(store, {
                      isLoading: false,
                      studySites: response.data,
                      totalStudySitesCount: response.total,
                      siteColumns: updatedColumns,
                    });
                    if (!query)
                      if (
                        !['us_international', 'canada'].includes(
                          regionFilter || '',
                        )
                      ) {
                        patchState(store, {
                          grandTotalStudySitesCount: response.total,
                          filteredStudySitesCount: response.total,
                        });
                      } else {
                        patchState(store, {
                          filteredStudySitesCount: response.total,
                        });
                      }
                  },
                }),
              ),
          ),
        ),
      );

      const _loadReviewLocations = rxMethod(
        pipe(
          tap(() => patchState(store, { isLoading: true, error: null })),
          switchMap(() =>
            _submissionSetupService.getReviewLocations().pipe(
              tapResponse({
                error: (error: Error) =>
                  patchState(store, { error, isLoading: false }),
                finalize: () => patchState(store, { isLoading: false }),
                next: (response) => {
                  patchState(store, {
                    isLoading: false,
                    reviewLocations: response.data,
                    totalReviewLocations: response.total,
                  });
                },
              }),
            ),
          ),
        ),
      );

      const _searchStudies = rxMethod<{
        page: number;
        pageSize: number;
        searchValue?: string;
        entityType?: 'study' | 'site' | 'study,site';
        gridView?: boolean;
      }>(
        pipe(
          tap(() => patchState(store, { studiesLoading: true, error: null })),
          switchMap(({ page, pageSize, searchValue, entityType, gridView }) => {
            const queryParams = {
              page,
              page_size: pageSize,
              grid_view: gridView || false,
              ...(searchValue && { query: searchValue }),
              ...(entityType && { entity_type: entityType }),
            };

            return _studyService.getStudies(queryParams).pipe(
              tapResponse({
                error: (error: Error) =>
                  patchState(store, { error, studiesLoading: false }),
                finalize: () => patchState(store, { studiesLoading: false }),
                next: (response) => {
                  const columns = sortColumnsByOrdinal(
                    { ...response.config.columns },
                    true,
                  );
                  // Disable sorting for all columns as it is not a requirement currently
                  const updatedColumns = updateColumnsSortable(columns, false);

                  patchState(store, {
                    studies: response.data,
                    studiesLoading: false,
                    totalStudies: response.total,
                    studyColumns: updatedColumns,
                  });
                },
              }),
            );
          }),
        ),
      );

      const _getSitesByWorkspaceRegion = rxMethod<{
        submissionId: string;
        queryParams?: Partial<SitesWorkspaceQP>;
      }>(
        pipe(
          tap(() => patchState(store, { isLoading: true, error: null })),
          switchMap(({ submissionId, queryParams }) =>
            _submissionSetupService
              .getSitesByWorkspaceRegion(submissionId, queryParams)
              .pipe(
                tapResponse({
                  error: (error: Error) =>
                    patchState(store, { error, isLoading: false }),
                  finalize: () => patchState(store, { isLoading: false }),
                  next: (response) => {
                    const columns = sortColumnsByOrdinal(
                      { ...response.config.columns },
                      true,
                    );
                    // Disable sorting for all columns as it is not a requirement currently
                    const updatedColumns = updateColumnsSortable(
                      columns,
                      false,
                    );
                    patchState(store, {
                      isLoading: false,
                      studySites: response.data,
                      totalStudySitesCount: response.total,
                      siteColumns: updatedColumns,
                    });
                    if (!queryParams?.query) {
                      if (
                        !['us_international', 'canada'].includes(
                          queryParams?.region_filter || '',
                        )
                      ) {
                        patchState(store, {
                          grandTotalStudySitesCount: response.total,
                          filteredStudySitesCount: response.total,
                        });
                      } else {
                        patchState(store, {
                          filteredStudySitesCount: response.total,
                        });
                      }
                    }
                  },
                }),
              ),
          ),
        ),
      );

      const _getSubmissionById = rxMethod<{ id: string }>(
        pipe(
          tap(() => patchState(store, { isLoading: true, error: null })),
          switchMap(({ id }) =>
            _subsmissionService.getSubmission(id).pipe(
              tapResponse({
                error: (error: Error) =>
                  patchState(store, { error, isLoading: false }),
                next: (response: SubmissionResponse) => {
                  patchState(store, {
                    isLoading: false,
                    submission: { ...response },
                    workspaceId: response.id,
                  });
                  // Automatically fetch sites by workspace region when submission is retrieved only if it is a multisite workspace
                  if (response.is_multisite) {
                    _getSitesByWorkspaceRegion({ submissionId: response.id });
                  }
                },
                finalize: () => patchState(store, { isLoading: false }),
              }),
            ),
          ),
        ),
      );

      return {
        loadWorkspaceTypes: (
          selectedTypeId?: string,
          params?: BaseQueryParams,
        ) => _loadWorkspaceTypes({ selectedTypeId, params }),
        loadWorkspaceSubTypes: (params?: WorkspaceSubTypeV2QP) =>
          _loadWorkspaceSubTypes({ params }),
        loadReviewLocations: () => _loadReviewLocations({}),
        selectWorkspaceType: (
          workspaceType: WorkspaceTypeSchema | WorkspaceTypeAdminSchema,
        ) => {
          patchState(store, { selectedWorkspaceType: workspaceType });
          const parentId = (workspaceType as WorkspaceTypeSchema)?.id;
          if (parentId) {
            _loadWorkspaceSubTypes({ params: { parent_id: parentId } });
          }
        },
        selectWorkspaceSubType: (workspaceSubType: WorkspaceSubTypeV2Schema) =>
          patchState(store, { selectedWorkspaceSubType: workspaceSubType }),
        createStudyWorkspace: (data: StudyWorkspaceCreate) =>
          _createStudyWorkspace(data),
        createSiteWorkspace: (data: SiteWorkspaceCreate) =>
          _createSiteWorkspace(data),
        createMTRWorkspace: (data: MTRWorkspaceCreate) =>
          _createMTRWorkspace(data),
        createAppealsWorkspace: (data: AppealsWorkspaceCreate) =>
          _createAppealWorkspace(data),
        constructSubmissionURL: (intId: number) =>
          _constructSubmissionURL(intId),
        getStudySites: (
          studyId: string,
          page: number,
          pageSize: number,
          query: string,
        ) => _getStudySites({ studyId, page, pageSize, query }),
        getSitesForRegion: (
          studyId: string,
          page: number,
          pageSize: number,
          query?: string,
          regionFilter?: RegionFilter,
        ) =>
          _getSitesForRegion({
            studyId,
            page,
            pageSize,
            query,
            regionFilter,
          }),
        clearSelection: () =>
          patchState(store, {
            selectedWorkspaceType: null,
            selectedWorkspaceSubType: null,
          }),
        clearError: () => patchState(store, { error: null }),
        fetchDuplicateStudiesByProtocalTitle: (
          title: string,
          sponsor: string,
        ) =>
          _studyService
            .fetchDuplicateStudiesByProtocalTitle({
              title,
              sponsor,
            })
            .pipe(map((response: { message: boolean }) => response.message)),
        searchStudies: (
          page: number,
          pageSize: number,
          searchValue?: string,
          entityType?: 'study' | 'site' | 'study,site',
        ) =>
          _searchStudies({
            page,
            pageSize,
            searchValue,
            entityType,
            gridView: false,
          }),
        reset: () => patchState(store, { ...SUBMISSION_SETUP_INITIAL_STATE }),
        getSubmissionById: (id: string) => _getSubmissionById({ id }),
        getSitesByWorkspaceRegion: (
          submissionId: string,
          queryParams?: Partial<SitesWorkspaceQP>,
        ) => _getSitesByWorkspaceRegion({ submissionId, queryParams }),
      };
    },
  ),
) satisfies Type<SubmissionSetupSignalState>;
